import { computed, ComputedRef } from '@vue/composition-api'
import { DefinitionList } from '@/types/uiComponents'
import { useBatteryStatus, useCurrentUserProfile, useUserProfile } from '@/modules/user/compositions'
import { useCasterCastStatus } from '@/modules/status/compositions'
import { UserRole } from '@/types/users'
import store from '@/store'
import roleNames from '@/roleNames'

const getDeviceColor = (deviceId: string, selected: string) => {
  return deviceId === selected ? 'dd-active' : 'dd-neutral'
}

const useCasterDiagnosticsDefLists = (casterId?: ComputedRef<string>) => {
  // Computed Props for Use Within Definition Lists //

  const casterIdRef = (casterId
    ?? useCurrentUserProfile()?.currentUserId
    ?? computed(() => '')) as ComputedRef<string>

  const profileRef = casterId?.value
    ? useUserProfile(casterId).userProfile
    : useCurrentUserProfile().currentUserProfile

  const profile = profileRef?.value
  const { chargingStatus, percentBattery } = useBatteryStatus(profileRef)
  const casterStatus = useCasterCastStatus(casterIdRef, computed(() => null))
  const stats = casterStatus.networkStats.value
  const { videoBitrate, audioBitrate, totalBitrate } = casterStatus.bitrates

  const chromeVersion = computed(() => {
    // @ts-ignore
    const major = profileRef?.value?.agent?.chrome_version?.major ?? 0
    // @ts-ignore
    const minor = profileRef?.value?.agent?.chrome_version?.minor ?? 0
    // @ts-ignore
    const build = profileRef?.value?.agent?.chrome_version?.build ?? 0
    // @ts-ignore
    const patch = profileRef?.value?.agent?.chrome_version?.patch ?? 0
    return `${major}.${minor}.${build}.${patch}`
  })
  const cameras = computed(() => {
    return profileRef?.value?.devices?.filter((device) => device.type === 'video').map((device) => device.name) ?? []
  })
  const microphones = computed(() => {
    return profileRef?.value?.devices?.filter((device) => device.type === 'audio').map((device) => device.name) ?? []
  })
  const outputs = computed(() => {
    return profileRef?.value?.devices?.filter((device) => device.type === 'output').map((device) => device.name) ?? []
  })

  const cameraStyles = computed(() => {
    return profileRef?.value?.devices?.filter((device) => device.type === 'video').map((device) => {
      return getDeviceColor(device.id, profile?.selectedVideoDevice ?? '')
    }) ?? []
  })
  const microphoneStyles = computed(() => {
    return profileRef?.value?.devices?.filter((device) => device.type === 'audio').map((device) => {
      return getDeviceColor(device.id, profile?.selectedAudioDevice ?? '')
    }) ?? []
  })
  const outputStyles = computed(() => {
    return outputs.value.map((device) => {
      return getDeviceColor(device, '')
    })
  })


  const role = computed(() => {
    if (profileRef?.value?.role === UserRole.SuperUser) return 'superuser'
    const member = store.state.team.team?.members[casterIdRef.value]
    if (member) return roleNames(member.role)
    return 'N/A'
  })

  // Definition Lists to Return //

  const profileDefList = computed<DefinitionList[]>(() => {
    return [
      { key: 'Name', values: [`${profile?.first_name} ${profile?.last_name}`] },
      { key: 'Display Name', values: [profile?.display_name ?? 'N/A'] },
      { key: 'Role', values: [role.value] },
      //@ts-ignore - no timezone prop in UserAgent
      { key: 'Timezone', values: [profile?.agent?.timezone ?? 'N/A'] },
      { key: 'ID', values: [profile?.id ?? 'N/A'] },
      //@ts-ignore - no ip prop in UserAgent
      { key: 'IP', values: [profile?.agent?.ipaddress ?? 'N/A'] }
    ]
  })
  const systemDefList = computed<DefinitionList[]>(() => {
    return [
      { key: 'Studio', values: [profile?.version ?? 'N/A'] },
      //@ts-ignore - no browser_language prop in UserAgent
      { key: 'Language', values: [profile?.agent?.browser_language ?? 'N/A'] },
      { key: 'Type', values: ['Chrome v' + chromeVersion.value] },
      //@ts-ignore - no platform prop in UserAgent
      { key: 'OS', values: [profile?.agent?.platform ?? 'N/A'] },
      //@ts-ignore - no cpus prop in UserAgent
      { key: 'CPU', values: [(profile?.agent?.cpus ?? 'N/A') + ' Cores'] },
      //@ts-ignore - no memory prop in UserAgent
      { key: 'RAM', values: [(profile?.agent?.memory ?? 'N/A') + ' GB'] },
      { key: 'Battery', values: [`${percentBattery.value} - ${chargingStatus.value}`] },
      { key: 'Cameras', values: cameras.value, valueClasses: cameraStyles.value },
      { key: 'Mic', values: microphones.value, valueClasses: microphoneStyles.value },
      { key: 'Outputs', values: outputs.value, valueClasses: outputStyles.value }
    ]
  })
  const encoderDefList = computed<DefinitionList[]>(() => {
    return [
      { key: 'CPU Usage', values: [stats?.encoder?.cpu_usage ? Math.round(stats.encoder.cpu_usage) + '%' : 'N/A'] },
      { key: 'Resolution', values: [stats?.encoder?.width && stats.encoder.height
        ? `${Math.round(stats?.encoder?.width)}x${Math.round(stats?.encoder?.height)}`
        : 'N/A'] },
      { key: 'Bitrate', values: [totalBitrate.value + ' kbps'] },
      { key: 'Video Bitrate', values: [videoBitrate.value + ' kbps'] },
      { key: 'Audio Bitrate', values: [audioBitrate.value + ' kbps'] },
      { key: 'Framerate', values: [stats?.encoder?.fps_out
      ? Math.round(stats.encoder.fps_out) + 'fps'
      : 'N/A'] }
    ]
  })

  return {
    profileDefList,
    systemDefList,
    encoderDefList
  }
}

export default useCasterDiagnosticsDefLists
